// src/pages/Employee/CreateEmployee/FilePreviewModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from 'reactstrap';
import { Download, AlertTriangle } from 'react-feather';
import { FileValue } from '../../types/employee.types';

interface FilePreviewModalProps {
  isOpen: boolean;
  toggle: () => void;
  title: string;
  fileValue: FileValue | null;
}

// Figures out whether we're looking at an image, a PDF, or something else,
// and produces a viewable object URL either from the local File or the
// existing server URL.
const usePreviewSource = (fileValue: FileValue | null) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (fileValue?.file) {
      const url = URL.createObjectURL(fileValue.file);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setObjectUrl(null);
    return undefined;
  }, [fileValue?.file]);

  return useMemo(() => {
    if (!fileValue) return { url: null, kind: 'none' as const };

    const url = objectUrl || fileValue.existingUrl;
    if (!url) return { url: null, kind: 'none' as const };

    const name = (fileValue.file?.name || fileValue.name || url).toLowerCase();
    const type = fileValue.file?.type || '';

    if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) {
      return { url, kind: 'image' as const };
    }
    if (type === 'application/pdf' || /\.pdf$/.test(name)) {
      return { url, kind: 'pdf' as const };
    }
    return { url, kind: 'other' as const };
  }, [fileValue, objectUrl]);
};

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ isOpen, toggle, title, fileValue }) => {
  const { url, kind } = usePreviewSource(fileValue);
  const [imgLoading, setImgLoading] = useState(true);

  useEffect(() => {
    if (isOpen) setImgLoading(true);
  }, [isOpen, url]);

  const displayName = fileValue?.file?.name || fileValue?.name || 'Attachment';

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody className="text-center" style={{ minHeight: 320 }}>
        {!url && (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
            <AlertTriangle size={28} className="mb-2" />
            <span>No file to preview yet</span>
          </div>
        )}

        {url && kind === 'image' && (
          <div className="position-relative">
            {imgLoading && (
              <div className="d-flex justify-content-center py-5">
                <Spinner color="primary" />
              </div>
            )}
            <img
              src={url}
              alt={displayName}
              onLoad={() => setImgLoading(false)}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                display: imgLoading ? 'none' : 'inline-block',
                borderRadius: 6,
                border: '1px solid #e9ebec',
              }}
            />
          </div>
        )}

        {url && kind === 'pdf' && (
          <embed
            src={url}
            type="application/pdf"
            style={{ width: '100%', height: '70vh', border: '1px solid #e9ebec', borderRadius: 6 }}
          />
        )}

        {url && kind === 'other' && (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
            <AlertTriangle size={28} className="mb-2" />
            <span>Preview isn't available for this file type.</span>
            <span className="small">{displayName}</span>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <span className="text-muted small me-auto text-truncate" style={{ maxWidth: 320 }}>
          {displayName}
        </span>
        {url && (
          <a
            href={url}
            download={displayName}
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary btn-sm"
          >
            <Download size={14} className="me-1" />
            Download
          </a>
        )}
        <Button color="secondary" size="sm" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FilePreviewModal;